(() => {
  const selector = '.sd-price-tooltip';
  const enhancedAttribute = 'data-sd-tooltip-enhanced';
  const openAttribute = 'data-sd-tooltip-open';
  const gap = 10;
  const viewportPadding = 12;
  const closeDelay = 180;
  let tooltipSequence = 0;
  let activeTooltip = null;
  let closeTimer = null;

  const clearCloseTimer = () => {
    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
  };

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

  const positionTooltip = (tooltip) => {
    if (!tooltip || tooltip.dataset.sdTooltipOpen !== 'true') return;

    const trigger = tooltip.querySelector('.sd-price-tooltip__trigger');
    const content = tooltip.querySelector('.sd-price-tooltip__content');
    if (!trigger || !content) return;

    const triggerRect = trigger.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;

    content.style.left = '0px';
    content.style.top = '0px';
    content.style.visibility = 'hidden';
    const contentRect = content.getBoundingClientRect();
    const contentWidth = contentRect.width;
    const contentHeight = contentRect.height;
    const maxLeft = Math.max(viewportPadding, viewportWidth - contentWidth - viewportPadding);
    const maxTop = Math.max(viewportPadding, viewportHeight - contentHeight - viewportPadding);

    const placements = [
      {
        name: 'right',
        left: triggerRect.right + gap,
        top: triggerRect.top + (triggerRect.height - contentHeight) / 2,
        fits: triggerRect.right + gap + contentWidth <= viewportWidth - viewportPadding,
      },
      {
        name: 'left',
        left: triggerRect.left - gap - contentWidth,
        top: triggerRect.top + (triggerRect.height - contentHeight) / 2,
        fits: triggerRect.left - gap - contentWidth >= viewportPadding,
      },
      {
        name: 'below',
        left: triggerRect.left,
        top: triggerRect.bottom + gap,
        fits: triggerRect.bottom + gap + contentHeight <= viewportHeight - viewportPadding,
      },
      {
        name: 'above',
        left: triggerRect.left,
        top: triggerRect.top - gap - contentHeight,
        fits: triggerRect.top - gap - contentHeight >= viewportPadding,
      },
    ];

    const placement = placements.find((candidate) => candidate.fits) || placements[2];
    const left = clamp(placement.left, viewportPadding, maxLeft);
    const top = clamp(placement.top, viewportPadding, maxTop);

    content.dataset.sdTooltipPlacement = placement.name;
    content.style.left = `${Math.round(left)}px`;
    content.style.top = `${Math.round(top)}px`;
    content.style.visibility = '';
  };

  const closeTooltip = (tooltip, restoreFocus = false) => {
    if (!tooltip) return;

    clearCloseTimer();
    tooltip.dataset.sdTooltipOpen = 'false';
    tooltip.style.zIndex = '';

    const trigger = tooltip.querySelector('.sd-price-tooltip__trigger');
    const content = tooltip.querySelector('.sd-price-tooltip__content');
    trigger?.setAttribute('aria-expanded', 'false');
    content?.setAttribute('aria-hidden', 'true');

    if (restoreFocus && trigger) trigger.focus();
    if (activeTooltip === tooltip) activeTooltip = null;
  };

  const scheduleClose = (tooltip) => {
    clearCloseTimer();
    closeTimer = window.setTimeout(() => {
      closeTooltip(tooltip);
    }, closeDelay);
  };

  const openTooltip = (tooltip) => {
    if (!tooltip) return;

    clearCloseTimer();
    if (activeTooltip && activeTooltip !== tooltip) closeTooltip(activeTooltip);
    activeTooltip = tooltip;
    tooltip.dataset.sdTooltipOpen = 'true';
    tooltip.style.zIndex = '1001';

    const trigger = tooltip.querySelector('.sd-price-tooltip__trigger');
    const content = tooltip.querySelector('.sd-price-tooltip__content');
    trigger?.setAttribute('aria-expanded', 'true');
    content?.setAttribute('aria-hidden', 'false');
    positionTooltip(tooltip);
  };

  const initialize = (root = document) => {
    root.querySelectorAll(selector).forEach((tooltip) => {
      if (tooltip.dataset.sdTooltipEnhanced === 'true') return;

      const trigger = tooltip.querySelector('.sd-price-tooltip__trigger');
      const content = tooltip.querySelector('.sd-price-tooltip__content');
      if (!trigger || !content) return;

      tooltip.dataset.sdTooltipEnhanced = 'true';
      const contentId = content.id || `sd-price-tooltip-${++tooltipSequence}`;
      content.id = contentId;
      content.setAttribute('aria-hidden', 'true');
      trigger.setAttribute('aria-controls', contentId);
      trigger.setAttribute('aria-expanded', 'false');

      tooltip.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') openTooltip(tooltip);
      });

      tooltip.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') scheduleClose(tooltip);
      });

      tooltip.addEventListener('focusin', () => openTooltip(tooltip));

      tooltip.addEventListener('focusout', (event) => {
        if (!tooltip.contains(event.relatedTarget)) scheduleClose(tooltip);
      });

      trigger.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse') {
          event.preventDefault();
          if (tooltip.dataset.sdTooltipOpen === 'true') {
            closeTooltip(tooltip);
          } else {
            openTooltip(tooltip);
          }
        }
      });
    });
  };

  document.addEventListener('pointerdown', (event) => {
    if (activeTooltip && !activeTooltip.contains(event.target)) closeTooltip(activeTooltip);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeTooltip) closeTooltip(activeTooltip, true);
  });

  window.addEventListener('resize', () => positionTooltip(activeTooltip), { passive: true });
  window.addEventListener('scroll', () => positionTooltip(activeTooltip), { passive: true, capture: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (activeTooltip && event.target.contains(activeTooltip)) closeTooltip(activeTooltip);
  });
})();
